import moment from 'moment'
import { fulcrumRequest } from './fulcrum'
import { FULCRUM_FORMS, FULCRUM_SQL_FIELDS } from '../constants/fulcrumForms'

const cache = {}
const dailyLogsByProject = {}
const recordsByRange = {}
const refreshListeners = new Set()
const loadedListeners = new Set()

function escapeSqlString(value) {
  return String(value).replace(/'/g, "''")
}

function parseQueryRows(data) {
  if (!data) {
    return []
  }
  if (Array.isArray(data)) {
    return data
  }
  if (data.rows) {
    return data.rows
  }
  if (data.features) {
    return data.features.map((feature) => feature.properties || feature)
  }
  return []
}

async function runFulcrumQuery(sql) {
  const { data } = await fulcrumRequest({ method: 'query', query: sql, format: 'json' })
  return parseQueryRows(data)
}

function buildDateRangeSql(formKey, dateFrom, dateTo, extraConditions = []) {
  const formId = FULCRUM_FORMS[formKey]
  const sqlFields = FULCRUM_SQL_FIELDS[formKey]
  if (!formId || !sqlFields) {
    throw new Error(`Unknown Fulcrum SQL config: ${formKey}`)
  }

  const conditions = [...extraConditions]

  if (dateFrom) {
    conditions.push(`"${sqlFields.dateField}" >= '${escapeSqlString(dateFrom)}'`)
  }
  if (dateTo) {
    conditions.push(`"${sqlFields.dateField}" <= '${escapeSqlString(dateTo)}'`)
  }

  const whereClause = conditions.length ? ` WHERE ${conditions.join(' AND ')}` : ''
  return `SELECT _record_id FROM "${formId}"${whereClause}`
}

function clearRangeCaches() {
  Object.keys(recordsByRange).forEach((key) => delete recordsByRange[key])
}

export function subscribeFulcrumRefresh(listener) {
  refreshListeners.add(listener)
  return () => refreshListeners.delete(listener)
}

export function subscribeFulcrumLoaded(listener) {
  loadedListeners.add(listener)
  return () => loadedListeners.delete(listener)
}

function notifyFulcrumLoaded() {
  loadedListeners.forEach((listener) => listener())
}

export function refreshFulcrumData(options = {}) {
  const { datasets } = options

  if (datasets && datasets.length > 0) {
    invalidateFulcrumCache(...datasets)
  } else {
    invalidateFulcrumCache()
  }

  refreshListeners.forEach((listener) => listener())
}

export function invalidateFulcrumCache(...keys) {
  if (keys.length === 0) {
    Object.keys(cache).forEach((key) => delete cache[key])
    dailyLogsByProjectCacheClear()
    clearRangeCaches()
    return
  }
  keys.forEach((key) => delete cache[key])
  if (keys.includes('DailyLogs')) {
    dailyLogsByProjectCacheClear()
  }
  if (keys.includes('Prestarts') || keys.includes('Workshop')) {
    clearRangeCaches()
  }
}

function dailyLogsByProjectCacheClear() {
  Object.keys(dailyLogsByProject).forEach((key) => delete dailyLogsByProject[key])
}

async function fetchRecordsForFormDateRange(formKey, dateFrom, dateTo, extraConditions = []) {
  const cacheKey = `${formKey}:${dateFrom || 'all'}:${dateTo || 'all'}:${extraConditions.join('|')}`
  if (recordsByRange[cacheKey]) {
    return recordsByRange[cacheKey]
  }

  const sql = buildDateRangeSql(formKey, dateFrom, dateTo, extraConditions)
  const rows = await runFulcrumQuery(sql)
  const recordIds = new Set(rows.map((row) => row._record_id).filter(Boolean))

  if (recordIds.size === 0) {
    recordsByRange[cacheKey] = []
    return []
  }

  const allRecords = await fetchFulcrumDataset(formKey)
  const filtered = allRecords.filter((record) => recordIds.has(record.id))
  recordsByRange[cacheKey] = filtered
  return filtered
}

export function fetchPrestartsForDateRange(dateFrom, dateTo) {
  return fetchRecordsForFormDateRange('Prestarts', dateFrom, dateTo)
}

export function fetchWorkshopMaintenanceRecords(dateFrom, dateTo) {
  const { sheetTypeField, sheetTypeValue } = FULCRUM_SQL_FIELDS.Workshop
  const extraConditions = [`"${sheetTypeField}" = '${escapeSqlString(sheetTypeValue)}'`]
  return fetchRecordsForFormDateRange('Workshop', dateFrom, dateTo, extraConditions)
}

export async function fetchDailyLogsForProject(projectId, onProgress) {
  if (!projectId) {
    return []
  }

  if (dailyLogsByProject[projectId]) {
    if (onProgress) {
      onProgress('done')
    }
    return dailyLogsByProject[projectId]
  }

  if (onProgress) {
    onProgress('loading')
  }

  const { data } = await fulcrumRequest({
    method: 'all',
    form_id: FULCRUM_FORMS.DailyLogs,
    options: { project_id: projectId },
  })

  dailyLogsByProject[projectId] = data
  if (onProgress) {
    onProgress('done')
  }
  return data
}

export async function fetchFulcrumDataset(key) {
  if (cache[key]) {
    return cache[key]
  }
  const form_id = FULCRUM_FORMS[key]
  if (!form_id) {
    throw new Error(`Unknown Fulcrum dataset: ${key}`)
  }
  const { data } = await fulcrumRequest({ method: 'all', form_id, options: {} })
  cache[key] = data
  notifyFulcrumLoaded()
  return data
}

export async function fetchFulcrumDatasets(keys, onProgress) {
  const uniqueKeys = [...new Set(keys)]
  const status = {}
  uniqueKeys.forEach((key) => {
    status[key] = cache[key] ? 'done' : 'pending'
  })

  const notify = () => {
    if (onProgress) {
      onProgress({ ...status })
    }
  }
  notify()

  const keysToFetch = uniqueKeys.filter((key) => !cache[key])
  await Promise.all(
    keysToFetch.map(async (key) => {
      status[key] = 'loading'
      notify()
      try {
        await fetchFulcrumDataset(key)
        status[key] = 'done'
        notify()
      } catch (error) {
        status[key] = 'error'
        notify()
        throw error
      }
    })
  )

  return uniqueKeys.reduce((acc, key) => {
    acc[key] = cache[key]
    return acc
  }, {})
}

export function getCachedFulcrumDatasets(keys) {
  const uniqueKeys = [...new Set(keys)]
  if (!uniqueKeys.every((key) => cache[key])) {
    return null
  }
  return uniqueKeys.reduce((acc, key) => {
    acc[key] = cache[key]
    return acc
  }, {})
}

export function buildBoreList(QualityPlans, user) {
  if (!QualityPlans || !user?.username) {
    return []
  }

  const boreListArray = []
  for (let i = 0; i < QualityPlans.length; i++) {
    const plan = QualityPlans[i]
    const isClientMatch =
      user.role === 'client' && plan.form_values['6f71'] === user.username
    const isAdmin = user.role === 'admin'

    if (!isAdmin && !isClientMatch) {
      continue
    }
    if (plan.project_id === null) {
      continue
    }

    let drilltecJobNumber = ''
    if (plan.form_values.b059) {
      drilltecJobNumber = plan.form_values.b059
    } else if (plan.form_values.c713) {
      drilltecJobNumber = plan.form_values.c713
    }

    const clientJobNumbers = plan.form_values['72a2'] || ''
    const searchFields = {
      'Job number': drilltecJobNumber,
      'Client job': clientJobNumbers,
      Title: plan.form_values.b62d,
      Location: plan.form_values['94d5'],
      Client: plan.form_values['6f71'],
      Status: plan.status,
      Driller: plan.form_values['9ae5']?.choice_values?.[0],
      Rig: plan.form_values['0a37']?.choice_values?.[0],
      Offsider: plan.form_values.fb65?.choice_values?.[0],
    }
    const searchText = Object.values(searchFields)
      .filter(Boolean)
      .join(' ')
      .toLowerCase()

    boreListArray.push({
      projectId: plan.project_id,
      jobNumber: drilltecJobNumber,
      label: drilltecJobNumber + (clientJobNumbers ? '  | ' + clientJobNumbers : ''),
      searchFields,
      searchText,
    })
  }

  if (boreListArray.length === 0) {
    return []
  }

  boreListArray.sort((a, b) => {
    if (a.jobNumber !== b.jobNumber) {
      return a.jobNumber < b.jobNumber ? -1 : 1
    }
    return a.projectId < b.projectId ? -1 : 1
  })
  const ret = [boreListArray[0]]
  for (let i = 1; i < boreListArray.length; i++) {
    if (boreListArray[i - 1].jobNumber !== boreListArray[i].jobNumber) {
      ret.push(boreListArray[i])
    }
  }
  ret.reverse()
  return ret
}

// Matches when every whitespace-separated token appears somewhere in the
// bore's searchText (job number, client job numbers, title, location,
// client, status, driller, rig, offsider).
export function filterBoreOption(input, option) {
  const haystack = option.props['data-search'] || ''
  return input
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((token) => haystack.includes(token))
}
